<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { CONCLUSION_EVENT_NAMES } from '@/presentlevels/no-ai/event_names'
import { CONCLUSION_ROUTE_NAMES } from '@/presentlevels/no-ai/conclusion_route_names'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { betaOptIn } from '@/util/beta_opt_in'

type ConclusionTab = {
  label: string
  routeName: string
  eventName: string
  isVisible: boolean
}

defineSlots<{
  default(): unknown
}>()

const route = useRoute()
const analytics = getAnalyticsTracker()
const { studentName, resourceDocs } = storeToRefs(usePresentLevelsWizardStore())
const { effectsVisibility } = storeToRefs(useVisibilityStore())

const hasResourceDocs = computed(() => resourceDocs.value.length > 0)

const tabs = computed((): ConclusionTab[] => [
  {
    label: 'Present Levels Statement',
    routeName: CONCLUSION_ROUTE_NAMES.STATEMENT,
    eventName: CONCLUSION_EVENT_NAMES.CLICKED_STATEMENT_TAB,
    isVisible: true
  },
  {
    label: 'Aligned Assessments',
    routeName: CONCLUSION_ROUTE_NAMES.ASSESSMENTS,
    eventName: CONCLUSION_EVENT_NAMES.CLICKED_ASSESSMENTS_TAB,
    isVisible: hasResourceDocs.value
  },
  {
    label: 'Aligned Goals',
    routeName: CONCLUSION_ROUTE_NAMES.GOALS_STRATEGIES,
    eventName: CONCLUSION_EVENT_NAMES.CLICKED_GOALS_TAB,
    isVisible: true
  }
])

const visibleTabs = computed(() => tabs.value.filter((tab) => tab.isVisible))

const activeRouteName = computed(() => {
  const routedTab = tabs.value.find((tab) => tab.routeName === route.name)
  return routedTab?.routeName ?? CONCLUSION_ROUTE_NAMES.STATEMENT
})

const isAssessmentsTabActive = computed(() => activeRouteName.value === CONCLUSION_ROUTE_NAMES.ASSESSMENTS)
const isGoalsTabActive = computed(() => activeRouteName.value === CONCLUSION_ROUTE_NAMES.GOALS_STRATEGIES)
const areEffectsHidden = computed(() => effectsVisibility.value === false)

const keptPageQuery = computed(() => betaOptIn(route.query))

// A link only follows itself on Enter, but a tab is also expected to activate on Space
function activateWithSpace(event: KeyboardEvent) {
  (event.currentTarget as HTMLElement).click()
}

function clickedTab(tab: ConclusionTab) {
  analytics.sendEvent(tab.eventName)
}
</script>

<template>
  <div>
    <h2 v-if="studentName" class="mb-5 text-center text-display-md font-medium leading-9">
      We drafted <span class="fs-exclude italic text-objective-blueAction">{{ studentName }}'s</span> present levels based on the information you provided.
    </h2>
    <p class="mx-auto min-h-[4em] text-center text-md text-gray-700" data-testid="conclusion-tab-description">
      <template v-if="isAssessmentsTabActive">
        <b>Save</b> or <b>download</b> the assessments below to collect student data that will inform your Present Levels Statement based on the information you provided.
      </template>
      <template v-else-if="isGoalsTabActive">
        <b>Browse</b> learning goals, objectives, and progress monitoring assessments aligned to your student's Present Levels Statement.
      </template>
      <template v-else-if="areEffectsHidden">
        <b>Copy</b> and <b>paste</b> the below information into your student's IEP. Additionally, click the tabs below to view recommended baseline assessments and goals based on the drafted statement.
      </template>
      <template v-else>
        <b>Copy</b> and <b>paste</b> the below information into your student's IEP, and edit based on your understanding of the student.
        To modify paragraphs about specific subject areas, or include additional subject areas, click "Edit Statements."
      </template>
    </p>

    <!-- #1c5498 is the anchor page accent, which has no design-system token yet -->
    <TabsRoot :model-value="activeRouteName" activation-mode="manual">
      <TabsList aria-label="Present levels conclusion" class="mb-4 flex items-end border-b-4 border-[#1c5498] pt-4">
        <TabsTrigger v-for="tab in visibleTabs" :key="tab.routeName" :value="tab.routeName" as-child>
          <router-link
            :to="{ name: tab.routeName, params: { uuid: route.params.uuid }, query: keptPageQuery }"
            class="block w-1/3 rounded-t-xs border border-transparent px-4 py-2.5 text-center text-lg font-bold text-objective-blue hover:border-gray-300 hover:bg-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 data-[state=active]:border-[#1c5498] data-[state=active]:bg-[#1c5498] data-[state=active]:text-white"
            :data-testid="`conclusion-tab-${tab.routeName}`"
            @click="clickedTab(tab)"
            @keydown.space.prevent="activateWithSpace"
          >{{ tab.label }}</router-link>
        </TabsTrigger>
      </TabsList>
      <TabsContent
        :value="activeRouteName"
        class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        <slot />
      </TabsContent>
    </TabsRoot>
  </div>
</template>
